import {
      getRelationshipBasedOnSex,
      getOppositeRelationship,
    } from './relationshipMappings'

    // Define family relationship groups
    const FAMILY_GROUPS = {
      SPOUSE: ['spouse', 'husband', 'wife'],
      PARENT: ['parent', 'father', 'mother'],
      CHILD: ['child', 'son', 'daughter'],
      SIBLING: ['sibling', 'brother', 'sister'],
      GRANDPARENT: ['grandparent', 'grandfather', 'grandmother'],
      GRANDCHILD: ['grandchild', 'grandson', 'granddaughter'],
      // New relationship groups
      PARTNER: ['partner', 'male partner', 'female partner'],
      COUSIN: ['cousin', 'male cousin', 'female cousin'],
      SECOND_COUSIN: ['second_cousin', 'male 2nd cousin', 'female 2nd cousin'],
      THIRD_COUSIN: ['third_cousin', 'male 3rd cousin', 'female 3rd cousin'],
      // Extended family
      PARENTAL_SIBLING: ['parental_sibling', 'uncle', 'aunt'], // Aunt/Uncle
      SIBLING_CHILD: ['sibling_child', 'nephew', 'niece'], // Niece/Nephew
      GRANDSIBLING_CHILD: [
        'grandsibling_child',
        'grandnephew',
        'grandniece',
      ], // Grandniece/Grandnephew
      // In-laws
      SIBLING_IN_LAW: ['sibling_in_law', 'brother-in-law', 'sister-in-law'],
      PARENT_IN_LAW: ['parent_in_law', 'father-in-law', 'mother-in-law'],
      CHILD_IN_LAW: ['child_in_law', 'son-in-law', 'daughter-in-law'],
      // Grandparent's sibling
      GRANDPARENT_SIBLING: [
        'grandparent_sibling',
        'great_uncle',
        'great_aunt',
      ], // Great-aunt/Great-uncle
      // Extended cousins
      FIRST_COUSIN_REMOVED: [
        'first_cousin_once_removed',
        '1st cousin once removed',
      ],
      SECOND_COUSIN_REMOVED: [
        'second_cousin_once_removed',
        '2nd cousin once removed',
      ],
      FIRST_COUSIN_TWICE_REMOVED: [
        'first_cousin_twice_removed',
        '1st cousin twice removed',
      ],
      // Step/half relationships
      STEP_PARENT: ['step_parent', 'stepfather', 'stepmother'],
      STEP_CHILD: ['step_child', 'stepson', 'stepdaughter'],
      STEP_SIBLING: ['step_sibling', 'stepbrother', 'stepsister'],
      HALF_SIBLING: ['half_sibling', 'half-brother', 'half-sister'],
      // Adoptive relationships
      ADOPTIVE_PARENT: [
        'adoptive_parent',
        'adoptive father',
        'adoptive mother',
      ],
      ADOPTIVE_CHILD: ['adoptive_child', 'adoptive son', 'adoptive daughter'],
      // Cousin-in-law
      COUSIN_IN_LAW: ['cousin_in_law', 'male cousin-in-law', 'female cousin-in-law'],
      // Niece/Nephew-in-law
      SIBLING_CHILD_IN_LAW: [
        'sibling_child_in_law',
        'nephew-in-law',
        'niece-in-law',
      ],
      PARENTAL_SIBLING_IN_LAW: [
        'parental_sibling_in_law',
        'uncle-in-law',
        'aunt-in-law',
      ],
    }

    // Check if a relationship type belongs to a family group
    const isRelationshipType = (relationshipType, group) => {
      if (!relationshipType) return false
      return FAMILY_GROUPS[group].includes(relationshipType.toLowerCase())
    }

    // Get all people in a specific relationship to a person
    const getPeopleInRelationship = (
      personId,
      relationshipType,
      allRelationships,
      allPeople
    ) => {
      console.log(
        `\n🔍 Looking for ${relationshipType} relationships for person ${personId}`
      )

      const relationships = allRelationships.filter((rel) => {
        const isPersonA = rel.person_a_id === personId
        const isPersonB = rel.person_b_id === personId

        if (isPersonA) {
          // Person is person_a, check relationship_type_b (what person_b is to person_a)
          const matches = isRelationshipType(
            rel.relationship_type_b,
            relationshipType
          )
          if (matches) {
            console.log(
              ` ✓ Found: ${rel.relationship_type_b} (person_b to person_a)`
            )
          }
          return matches
        } else if (isPersonB) {
          // Person is person_b, check relationship_type (what person_a is to person_b)
          const matches = isRelationshipType(
            rel.relationship_type,
            relationshipType
          )
          if (matches) {
            console.log(
              ` ✓ Found: ${rel.relationship_type} (person_a to person_b)`
            )
          }
          return matches
        }
        return false
      })

      console.log(` 📊 Found ${relationships.length} ${relationshipType} relationships`)

      return relationships
        .map((rel) => {
          const otherPersonId =
            rel.person_a_id === personId ? rel.person_b_id : rel.person_a_id
          const otherPerson = allPeople.find((p) => p.id === otherPersonId)
          const relationshipTypeFromPerson =
            rel.person_a_id === personId ? rel.relationship_type_b : rel.relationship_type
          if (otherPerson) {
            console.log(` 👤 ${otherPerson.name} (${relationshipTypeFromPerson})`)
          }
          return {
            person: otherPerson,
            relationshipId: rel.id,
            relationshipType: relationshipTypeFromPerson,
            otherPersonId,
          }
        })
        .filter((item) => item.person) // Filter out any missing people
    }

    // Generate smart family relationships when a new relationship is added
    export const generateSmartFamilyRelationships = (
      primaryPersonId,
      newPersonId,
      relationshipType,
      allRelationships,
      allPeople
    ) => {
      const newRelationships = []
      const primaryPerson = allPeople.find((p) => p.id === primaryPersonId)
      const newPerson = allPeople.find((p) => p.id === newPersonId)

      if (!primaryPerson || !newPerson) {
        console.log('❌ Could not find both people for smart relationships')
        return newRelationships
      }

      const plannedRelationshipExists = (personAId, personBId) =>
        newRelationships.some(
          (rel) => rel.person_a_id === personAId && rel.person_b_id === personBId
        )

      console.log(`\n🧠===GENERATING SMART RELATIONSHIPS===`)
      console.log(`👤 Primary: ${primaryPerson.name} (${primaryPersonId})`)
      console.log(`👤 New: ${newPerson.name} (${newPersonId})`)
      console.log(`🔗 Relationship: ${relationshipType}`)
      console.log(`📊 Total existing relationships: ${allRelationships.length}`)

      // SPOUSE LOGIC - Create parent-child relationships
      if (isRelationshipType(relationshipType, 'SPOUSE')) {
        console.log('\n💑 --- PROCESSING SPOUSE RELATIONSHIP ---')
        // Get primary person's children
        const primaryChildren = getPeopleInRelationship(
          primaryPersonId,
          'CHILD',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${primaryChildren.length} children`)

        // Get new spouse's children
        const spouseChildren = getPeopleInRelationship(
          newPersonId,
          'CHILD',
          allRelationships,
          allPeople
        )
        console.log(`${newPerson.name} has ${spouseChildren.length} children`)

        // Make primary person's children also children of the new spouse
        primaryChildren.forEach((childData) => {
          const childPerson = childData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, childPerson.id, allRelationships)
          ) {
            console.log(`➕ Making ${childPerson.name} a child of ${newPerson.name}`)
            newRelationships.push({
              person_a_id: newPersonId, // spouse (parent)
              person_b_id: childPerson.id, // child
              relationshipType: 'child', // what child is to spouse
              explanation: `${childPerson.name} becomes ${newPerson.name}'s child`,
            })
          } else {
            console.log(
              `⚠️ ${childPerson.name} is already connected to ${newPerson.name}`
            )
          }
        })

        // Make new spouse's children also children of primary person
        spouseChildren.forEach((childData) => {
          const childPerson = childData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(primaryPersonId, childPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${childPerson.name} a child of ${primaryPerson.name}`
            )
            newRelationships.push({
              person_a_id: primaryPersonId, // primary person (parent)
              person_b_id: childPerson.id, // child
              relationshipType: 'child', // what child is to primary person
              explanation: `${childPerson.name} becomes ${primaryPerson.name}'s child`,
            })
          } else {
            console.log(
              `⚠️ ${childPerson.name} is already connected to ${primaryPerson.name}`
            )
          }
        })

        // Make all children siblings to each other
        const allChildren = [...primaryChildren, ...spouseChildren]
        console.log(`👫 Making ${allChildren.length} children siblings to each other`)
        for (let i = 0; i < allChildren.length; i++) {
          for (let j = i + 1; j < allChildren.length; j++) {
            const child1 = allChildren[i].person
            const child2 = allChildren[j].person
            console.log(
              ` 🔍 Checking if ${child1.name} and ${child2.name} are already siblings`
            )
            // CRITICAL: Check if relationship already exists
            if (!relationshipExists(child1.id, child2.id, allRelationships)) {
              console.log(` ➕ Making ${child1.name} and ${child2.name} siblings`)
              newRelationships.push({
                person_a_id: child1.id,
                person_b_id: child2.id,
                relationshipType: 'sibling',
                explanation: `${child1.name} and ${child2.name} become siblings`,
              })
            } else {
              console.log(
                ` ⚠️ ${child1.name} and ${child2.name} are already connected`
              )
            }
          }
        }

        // Get primary person's siblings
        const primarySiblings = getPeopleInRelationship(
          primaryPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        )
        console.log(
          `${primaryPerson.name} has ${primarySiblings.length} sibling(s)`
        )

        // Make new spouse siblings-in-law to primary person's siblings
        primarySiblings.forEach((siblingData) => {
          const siblingPerson = siblingData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, siblingPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} a sibling-in-law to ${siblingPerson.name}`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: siblingPerson.id,
              relationshipType: 'sibling_in_law',
              explanation: `${newPerson.name} becomes ${siblingPerson.name}'s sibling-in-law`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${siblingPerson.name}`
            )
          }

          // Connect spouse to sibling's children (nieces/nephews-in-law)
          const niecesAndNephews = getPeopleInRelationship(
            siblingPerson.id,
            'CHILD',
            allRelationships,
            allPeople
          )

          niecesAndNephews.forEach((nephewData) => {
            const nephewPerson = nephewData.person
            if (
              !relationshipExists(newPersonId, nephewPerson.id, allRelationships)
            ) {
              console.log(
                `➕ Making ${newPerson.name} an aunt/uncle-in-law to ${nephewPerson.name}`
              )
              newRelationships.push({
                person_a_id: newPersonId, // The new spouse (aunt/uncle-in-law)
                person_b_id: nephewPerson.id, // The niece/nephew
                relationshipType: 'sibling_child_in_law', // What nephew is to spouse
                explanation: `${newPerson.name} becomes ${nephewPerson.name}'s aunt/uncle-in-law`,
              })
            } else {
              console.log(
                `⚠️ ${newPerson.name} is already connected to ${nephewPerson.name}`
              )
            }
          })
        })

        // Get new spouse's siblings
        const spouseSiblings = getPeopleInRelationship(
          newPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        )
        console.log(`${newPerson.name} has ${spouseSiblings.length} sibling(s)`)

        // Make primary person siblings-in-law to new spouse's siblings
        spouseSiblings.forEach((siblingData) => {
          const siblingPerson = siblingData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              primaryPersonId,
              siblingPerson.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${primaryPerson.name} a sibling-in-law to ${siblingPerson.name}`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: siblingPerson.id,
              relationshipType: 'sibling_in_law',
              explanation: `${primaryPerson.name} becomes ${siblingPerson.name}'s sibling-in-law`,
            })
          } else {
            console.log(
              `⚠️ ${primaryPerson.name} is already connected to ${siblingPerson.name}`
            )
          }

          // Connect primary person to spouse's sibling's children (nieces/nephews-in-law)
          const niecesAndNephews = getPeopleInRelationship(
            siblingPerson.id,
            'CHILD',
            allRelationships,
            allPeople
          )
          niecesAndNephews.forEach((nephewData) => {
            const nephewPerson = nephewData.person
            if (
              !relationshipExists(
                primaryPersonId,
                nephewPerson.id,
                allRelationships
              )
            ) {
              console.log(
                `➕ Making ${primaryPerson.name} an aunt/uncle-in-law to ${nephewPerson.name}`
              )
              newRelationships.push({
                person_a_id: primaryPersonId, // The primary person (aunt/uncle-in-law)
                person_b_id: nephewPerson.id, // The niece/nephew
                relationshipType: 'sibling_child_in_law', // What nephew is to primary person
                explanation: `${primaryPerson.name} becomes ${nephewPerson.name}'s aunt/uncle-in-law`,
              })
            } else {
              console.log(
                `⚠️ ${primaryPerson.name} is already connected to ${nephewPerson.name}`
              )
            }
          })
        })

        // Connect new spouse with primary person's parents (in-laws)
        const primaryParents = getPeopleInRelationship(
          primaryPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${primaryParents.length} parent(s)`)
        primaryParents.forEach((parentData) => {
          const parentPerson = parentData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, parentPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} a child-in-law to ${parentPerson.name}`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: parentPerson.id,
              relationshipType: 'parent_in_law',
              explanation: `${newPerson.name} becomes ${parentPerson.name}'s child-in-law`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${parentPerson.name}`
            )
          }
        })

        // Connect new spouse with primary person's aunts/uncles as niece/nephew-in-law
        primaryParents.forEach((parentData) => {
          const parentPerson = parentData.person
          const parentSiblings = getPeopleInRelationship(
            parentPerson.id,
            'SIBLING',
            allRelationships,
            allPeople
          )
          console.log(
            `${parentPerson.name} has ${parentSiblings.length} sibling(s) (aunts/uncles to ${primaryPerson.name})`
          )

          parentSiblings.forEach((siblingData) => {
            const auntUncle = siblingData.person
            const siblingChildInLawLabel = getRelationshipBasedOnSex(
              'sibling_child_in_law',
              newPerson.sex
            )
            if (
              !relationshipExists(auntUncle.id, newPersonId, allRelationships) &&
              !plannedRelationshipExists(auntUncle.id, newPersonId)
            ) {
              console.log(
                `➕ Making ${newPerson.name} a ${siblingChildInLawLabel} to ${auntUncle.name}`
              )
              newRelationships.push({
                person_a_id: auntUncle.id,
                person_b_id: newPersonId,
                relationshipType: 'sibling_child_in_law',
                explanation: `${newPerson.name} becomes ${auntUncle.name}'s ${siblingChildInLawLabel}`,
              })
            } else {
              console.log(
                `⚠️ ${newPerson.name} is already connected to ${auntUncle.name}`
              )
            }

            const auntUncleSpouses = getPeopleInRelationship(
              auntUncle.id,
              'SPOUSE',
              allRelationships,
              allPeople
            )
            auntUncleSpouses.forEach((spouseData) => {
              const spousePerson = spouseData.person
              if (
                !relationshipExists(
                  spousePerson.id,
                  newPersonId,
                  allRelationships
                ) &&
                !plannedRelationshipExists(spousePerson.id, newPersonId)
              ) {
                console.log(
                  `➕ Making ${newPerson.name} a ${siblingChildInLawLabel} to ${spousePerson.name}`
                )
                newRelationships.push({
                  person_a_id: spousePerson.id,
                  person_b_id: newPersonId,
                  relationshipType: 'sibling_child_in_law',
                  explanation: `${newPerson.name} becomes ${spousePerson.name}'s ${siblingChildInLawLabel}`,
                })
              } else {
                console.log(
                  `⚠️ ${newPerson.name} is already connected to ${spousePerson.name}`
                )
              }
            })
          })
        })

        // Connect primary person with new spouse's parents (in-laws)
        const spouseParents = getPeopleInRelationship(
          newPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(`${newPerson.name} has ${spouseParents.length} parent(s)`)
        spouseParents.forEach((parentData) => {
          const parentPerson = parentData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              primaryPersonId,
              parentPerson.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${primaryPerson.name} a child-in-law to ${parentPerson.name}`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: parentPerson.id,
              relationshipType: 'parent_in_law',
              explanation: `${primaryPerson.name} becomes ${parentPerson.name}'s child-in-law`,
            })
          } else {
            console.log(
              `⚠️ ${primaryPerson.name} is already connected to ${parentPerson.name}`
            )
          }
        })
      }

      // PARENT LOGIC - Create family connections
      else if (isRelationshipType(relationshipType, 'PARENT')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING PARENT RELATIONSHIP ---')
        // Get the parent's spouse(s)
        const parentSpouses = getPeopleInRelationship(
          newPersonId,
          'SPOUSE',
          allRelationships,
          allPeople
        )
        console.log(`${newPerson.name} has ${parentSpouses.length} spouse(s)`)

        // Make primary person a child of parent's spouse(s)
        parentSpouses.forEach((spouseData) => {
          const spousePerson = spouseData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              spousePerson.id,
              primaryPersonId,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${primaryPerson.name} a child of ${spousePerson.name}`
            )
            newRelationships.push({
              person_a_id: spousePerson.id, // spouse (parent)
              person_b_id: primaryPersonId, // primary person (child)
              relationshipType: 'child', // what primary person is to spouse
              explanation: `${primaryPerson.name} becomes ${spousePerson.name}'s child`,
            })
          } else {
            console.log(
              `⚠️ ${primaryPerson.name} is already connected to ${spousePerson.name}`
            )
          }
        })

        // Get parent's other children (primary person's new siblings)
        const parentChildren = getPeopleInRelationship(
          newPersonId,
          'CHILD',
          allRelationships,
          allPeople
        ).filter((childData) => childData.person.id !== primaryPersonId) // Exclude the primary person
        console.log(
          `${newPerson.name} has ${parentChildren.length} other children`
        )

        // Make primary person siblings with parent's other children
        parentChildren.forEach((childData) => {
          const siblingPerson = childData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              primaryPersonId,
              siblingPerson.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${primaryPerson.name} and ${siblingPerson.name} siblings`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: siblingPerson.id,
              relationshipType: 'sibling',
              explanation: `${primaryPerson.name} and ${siblingPerson.name} become siblings`,
            })
          } else {
            console.log(
              `⚠️ ${primaryPerson.name} and ${siblingPerson.name} are already connected`
            )
          }
        })

        // Connect with parent's parents (grandparents)
        const grandParents = getPeopleInRelationship(
          newPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(
          `${newPerson.name} has ${grandParents.length} parent(s) (grandparents to primary person)`
        )
        grandParents.forEach((grandParentData) => {
          const grandParent = grandParentData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              primaryPersonId,
              grandParent.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${grandParent.name} a grandparent to ${primaryPerson.name}`
            )
            newRelationships.push({
              person_a_id: grandParent.id,
              person_b_id: primaryPersonId,
              relationshipType: 'grandchild',
              explanation: `${grandParent.name} becomes ${primaryPerson.name}'s grandparent`,
            })
          } else {
            console.log(
              `⚠️ ${grandParent.name} is already connected to ${primaryPerson.name}`
            )
          }
        })

        // Connect with parent's siblings (aunts/uncles)
        const parentSiblings = getPeopleInRelationship(
          newPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        )
        console.log(
          `${newPerson.name} has ${parentSiblings.length} sibling(s) (aunts/uncles to primary person)`
        )
        parentSiblings.forEach((siblingData) => {
          const auntUncle = siblingData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(primaryPersonId, auntUncle.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${auntUncle.name} an aunt/uncle to ${primaryPerson.name}`
            )
            newRelationships.push({
              person_a_id: auntUncle.id, // The aunt/uncle
              person_b_id: primaryPersonId, // The niece/nephew
              relationshipType: 'sibling_child', // What primary person is to aunt/uncle
              explanation: `${auntUncle.name} becomes ${primaryPerson.name}'s aunt/uncle`,
            })
          } else {
            console.log(
              `⚠️ ${auntUncle.name} is already connected to ${primaryPerson.name}`
            )
          }
        })

        // DEEPER GENERATION CONNECTIONS
        // Connect parent's siblings' children (cousins) to primary person
        parentSiblings.forEach((siblingData) => {
          const auntUncle = siblingData.person
          const cousins = getPeopleInRelationship(
            auntUncle.id,
            'CHILD',
            allRelationships,
            allPeople
          )
          console.log(
            `${auntUncle.name} has ${cousins.length} child(ren) (cousins to ${primaryPerson.name})`
          )
          cousins.forEach((cousinData) => {
            const cousin = cousinData.person
            // CRITICAL: Check if relationship already exists
            if (
              !relationshipExists(primaryPersonId, cousin.id, allRelationships)
            ) {
              console.log(
                `➕ Making ${cousin.name} and ${primaryPerson.name} first cousins`
              )
              newRelationships.push({
                person_a_id: primaryPersonId,
                person_b_id: cousin.id,
                relationshipType: 'cousin',
                explanation: `${primaryPerson.name} and ${cousin.name} become first cousins`,
              })
            } else {
              console.log(
                `⚠️ ${primaryPerson.name} and ${cousin.name} are already connected`
              )
            }
          })
        })

        // Connect parent's parents' other children (great-aunts/uncles) to primary person
        grandParents.forEach((grandParentData) => {
          const grandParent = grandParentData.person
          const grandParentSiblings = getPeopleInRelationship(
            grandParent.id,
            'CHILD',
            allRelationships,
            allPeople
          ).filter((childData) => childData.person.id !== newPersonId) // Exclude the parent
          console.log(
            `${grandParent.name} has ${grandParentSiblings.length} other child(ren) (great-aunts/uncles to ${primaryPerson.name})`
          )
          grandParentSiblings.forEach((siblingData) => {
            const greatAuntUncle = siblingData.person
            // CRITICAL: Check if relationship already exists
            if (
              !relationshipExists(
                primaryPersonId,
                greatAuntUncle.id,
                allRelationships
              )
            ) {
              console.log(
                `➕ Making ${greatAuntUncle.name} a great-aunt/uncle to ${primaryPerson.name}`
              )
              newRelationships.push({
                person_a_id: greatAuntUncle.id,
                person_b_id: primaryPersonId,
                relationshipType: 'grandsibling_child',
                explanation: `${greatAuntUncle.name} becomes ${primaryPerson.name}'s great-aunt/uncle`,
              })
            } else {
              console.log(
                `⚠️ ${greatAuntUncle.name} is already connected to ${primaryPerson.name}`
              )
            }
          })
        })
      }

      // CHILD LOGIC - Create family connections
      else if (isRelationshipType(relationshipType, 'CHILD')) {
        console.log('\n👶 --- PROCESSING CHILD RELATIONSHIP ---')
        // Get the primary person's spouse(s)
        const primarySpouses = getPeopleInRelationship(
          primaryPersonId,
          'SPOUSE',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${primarySpouses.length} spouse(s)`)

        // Make the child also a child of primary person's spouse(s)
        primarySpouses.forEach((spouseData) => {
          const spousePerson = spouseData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(spousePerson.id, newPersonId, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} a child of ${spousePerson.name}`
            )
            newRelationships.push({
              person_a_id: spousePerson.id, // spouse (parent)
              person_b_id: newPersonId, // child
              relationshipType: 'child', // what child is to spouse
              explanation: `${newPerson.name} becomes ${spousePerson.name}'s child`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${spousePerson.name}`
            )
          }
        })

        // Get primary person's other children (new child's siblings)
        const primaryChildren = getPeopleInRelationship(
          primaryPersonId,
          'CHILD',
          allRelationships,
          allPeople
        ).filter((childData) => childData.person.id !== newPersonId) // Exclude the new child
        console.log(
          `${primaryPerson.name} has ${primaryChildren.length} other children`
        )

        // Make new child siblings with primary person's other children
        primaryChildren.forEach((childData) => {
          const siblingPerson = childData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, siblingPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} and ${siblingPerson.name} siblings`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: siblingPerson.id,
              relationshipType: 'sibling',
              explanation: `${newPerson.name} and ${siblingPerson.name} become siblings`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} and ${siblingPerson.name} are already connected`
            )
          }
        })

        // Connect child with primary person's parents (grandparents)
        const primaryParents = getPeopleInRelationship(
          primaryPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(
          `${primaryPerson.name} has ${primaryParents.length} parent(s) (grandparents to new child)`
        )
        primaryParents.forEach((parentData) => {
          const grandParent = parentData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, grandParent.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${grandParent.name} a grandparent to ${newPerson.name}`
            )
            newRelationships.push({
              person_a_id: grandParent.id,
              person_b_id: newPersonId,
              relationshipType: 'grandchild',
              explanation: `${grandParent.name} becomes ${newPerson.name}'s grandparent`,
            })
          } else {
            console.log(
              `⚠️ ${grandParent.name} is already connected to ${newPerson.name}`
            )
          }
        })

        // Connect child with primary person's siblings (aunts/uncles)
        const primarySiblings = getPeopleInRelationship(
          primaryPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        )
        console.log(
          `${primaryPerson.name} has ${primarySiblings.length} sibling(s) (aunts/uncles to new child)`
        )
        primarySiblings.forEach((siblingData) => {
          const auntUncle = siblingData.person
          // CRITICAL: Check if relationship already exists
          if (!relationshipExists(newPersonId, auntUncle.id, allRelationships)) {
            console.log(
              `➕ Making ${auntUncle.name} an aunt/uncle to ${newPerson.name}`
            )
            newRelationships.push({
              person_a_id: auntUncle.id, // The aunt/uncle
              person_b_id: newPersonId, // The child (niece/nephew)
              relationshipType: 'sibling_child', // What new person is to aunt/uncle
              explanation: `${auntUncle.name} becomes ${newPerson.name}'s aunt/uncle`,
            })
          } else {
            console.log(
              `⚠️ ${auntUncle.name} is already connected to ${newPerson.name}`
            )
          }

          // Connect child to aunt/uncle's spouse (aunt/uncle-in-law)
          const auntUncleSpouses = getPeopleInRelationship(
            auntUncle.id,
            'SPOUSE',
            allRelationships,
            allPeople
          )
          auntUncleSpouses.forEach((spouseData) => {
            const spousePerson = spouseData.person
            if (
              !relationshipExists(newPersonId, spousePerson.id, allRelationships)
            ) {
              console.log(
                `➕ Making ${spousePerson.name} an aunt/uncle-in-law to ${newPerson.name}`
              )
              newRelationships.push({
                person_a_id: spousePerson.id,
                person_b_id: newPersonId,
                relationshipType: 'sibling_child_in_law', // what the child is to the spouse (niece/nephew-in-law)
                explanation: `${spousePerson.name} becomes ${newPerson.name}'s aunt/uncle-in-law`,
              })
            } else {
              console.log(
                `⚠️ ${spousePerson.name} is already connected to ${newPerson.name}`
              )
            }
          })
        })

        // DEEPER GENERATION CONNECTIONS
        // Connect child with primary person's parents' siblings (great-aunts/uncles)
        primaryParents.forEach((parentData) => {
          const grandParent = parentData.person
          const grandParentSiblings = getPeopleInRelationship(
            grandParent.id,
            'SIBLING',
            allRelationships,
            allPeople
          )
          console.log(
            `${grandParent.name} has ${grandParentSiblings.length} sibling(s) (great-aunts/uncles to ${newPerson.name})`
          )
          grandParentSiblings.forEach((siblingData) => {
            const greatAuntUncle = siblingData.person
            // CRITICAL: Check if relationship already exists
            if (
              !relationshipExists(
                newPersonId,
                greatAuntUncle.id,
                allRelationships
              )
            ) {
              console.log(
                `➕ Making ${greatAuntUncle.name} a great-aunt/uncle to ${newPerson.name}`
              )
              newRelationships.push({
                person_a_id: greatAuntUncle.id,
                person_b_id: newPersonId,
                relationshipType: 'grandsibling_child',
                explanation: `${greatAuntUncle.name} becomes ${newPerson.name}'s great-aunt/uncle`,
              })
            } else {
              console.log(
                `⚠️ ${greatAuntUncle.name} is already connected to ${newPerson.name}`
              )
            }
          })
        })

        // Connect child with primary person's siblings' children (cousins)
        primarySiblings.forEach((siblingData) => {
          const auntUncle = siblingData.person
          const cousins = getPeopleInRelationship(
            auntUncle.id,
            'CHILD',
            allRelationships,
            allPeople
          )
          console.log(
            `${auntUncle.name} has ${cousins.length} child(ren) (cousins to ${newPerson.name})`
          )
          cousins.forEach((cousinData) => {
            const cousin = cousinData.person
            // CRITICAL: Check if relationship already exists
            if (!relationshipExists(newPersonId, cousin.id, allRelationships)) {
              console.log(
                `➕ Making ${newPerson.name} and ${cousin.name} first cousins`
              )
              newRelationships.push({
                person_a_id: newPersonId,
                person_b_id: cousin.id,
                relationshipType: 'cousin',
                explanation: `${newPerson.name} and ${cousin.name} become first cousins`,
              })
            } else {
              console.log(
                `⚠️ ${newPerson.name} and ${cousin.name} are already connected`
              )
            }
          })
        })
      }

      // SIBLING LOGIC - Create family connections
      else if (isRelationshipType(relationshipType, 'SIBLING')) {
        console.log('\n👫 --- PROCESSING SIBLING RELATIONSHIP ---')
        const processSiblingConnections = (person1Id, person2Id) => {
          const connections = []
          const person1 = allPeople.find((p) => p.id === person1Id)
          const person2 = allPeople.find((p) => p.id === person2Id)
          if (!person1 || !person2) return []
          console.log(
            `\n🔄 Processing connections for ${person1.name} -> adding ${person2.name}`
          )

          // 1. Connect person2 to person1's parents
          const parents = getPeopleInRelationship(
            person1Id,
            'PARENT',
            allRelationships,
            allPeople
          )
          parents.forEach((parentData) => {
            if (
              !relationshipExists(parentData.person.id, person2Id, [
                ...allRelationships,
                ...newRelationships,
                ...connections,
              ])
            ) {
              connections.push({
                person_a_id: parentData.person.id,
                person_b_id: person2Id,
                relationshipType: 'child',
                explanation: `${person2.name} becomes child of ${parentData.person.name}`,
              })
            }
          })

          // 2. Connect person2 to person1's spouse (as sibling-in-law)
          const spouses = getPeopleInRelationship(
            person1Id,
            'SPOUSE',
            allRelationships,
            allPeople
          )
          spouses.forEach((spouseData) => {
            if (
              !relationshipExists(spouseData.person.id, person2Id, [
                ...allRelationships,
                ...newRelationships,
                ...connections,
              ])
            ) {
              connections.push({
                person_a_id: spouseData.person.id,
                person_b_id: person2Id,
                relationshipType: 'sibling_in_law',
                explanation: `${person2.name} becomes sibling-in-law to ${spouseData.person.name}`,
              })
            }
          })

          // 3. Connect person2 to person1's children (as uncle/aunt)
          const children = getPeopleInRelationship(
            person1Id,
            'CHILD',
            allRelationships,
            allPeople
          )
          children.forEach((childData) => {
            if (
              !relationshipExists(childData.person.id, person2Id, [
                ...allRelationships,
                ...newRelationships,
                ...connections,
              ])
            ) {
              connections.push({
                person_a_id: person2Id,
                person_b_id: childData.person.id,
                relationshipType: 'sibling_child',
                explanation: `${person2.name} becomes uncle/aunt to ${childData.person.name}`,
              })
            }
            // Connect person2 to the child's spouse (as uncle/aunt-in-law)
            const childSpouses = getPeopleInRelationship(
              childData.person.id,
              'SPOUSE',
              allRelationships,
              allPeople
            )
            childSpouses.forEach((spouseData) => {
              const spousePerson = spouseData.person
              if (
                !relationshipExists(spousePerson.id, person2Id, [
                  ...allRelationships,
                  ...newRelationships,
                  ...connections,
                ])
              ) {
                connections.push({
                  person_a_id: person2Id,
                  person_b_id: spousePerson.id,
                  relationshipType: 'sibling_child_in_law',
                  explanation: `${person2.name} becomes uncle/aunt-in-law to ${spousePerson.name}`,
                })
              }
            })
          })

          // 4. Connect person2 to person1's grandparents
          const grandparents = getPeopleInRelationship(
            person1Id,
            'GRANDPARENT',
            allRelationships,
            allPeople
          )
          grandparents.forEach((gpData) => {
            if (
              !relationshipExists(gpData.person.id, person2Id, [
                ...allRelationships,
                ...newRelationships,
                ...connections,
              ])
            ) {
              connections.push({
                person_a_id: gpData.person.id,
                person_b_id: person2Id,
                relationshipType: 'grandchild',
                explanation: `${person2.name} becomes grandchild of ${gpData.person.name}`,
              })
            }
          })

          // 5. Connect person2 to person1's nieces/nephews (as uncle/aunt)
          const niecesNephews = getPeopleInRelationship(
            person1Id,
            'SIBLING_CHILD',
            allRelationships,
            allPeople
          )
          niecesNephews.forEach((nnData) => {
            if (
              !relationshipExists(nnData.person.id, person2Id, [
                ...allRelationships,
                ...newRelationships,
                ...connections,
              ])
            ) {
              connections.push({
                person_a_id: person2Id,
                person_b_id: nnData.person.id,
                relationshipType: 'sibling_child',
                explanation: `${person2.name} becomes uncle/aunt to ${nnData.person.name}`,
              })
            }
          })

          // 6. Connect person2 to person1's aunts/uncles (as nephew/niece)
          const auntsUncles = getPeopleInRelationship(
            person1Id,
            'PARENTAL_SIBLING',
            allRelationships,
            allPeople
          )
          auntsUncles.forEach((auData) => {
            if (
              !relationshipExists(auData.person.id, person2Id, [
                ...allRelationships,
                ...newRelationships,
                ...connections,
              ])
            ) {
              connections.push({
                person_a_id: auData.person.id,
                person_b_id: person2Id,
                relationshipType: 'sibling_child',
                explanation: `${person2.name} becomes nephew/niece to ${auData.person.name}`,
              })
            }
          })

          // DEEPER GENERATION CONNECTIONS
          // 7. Connect person2 to person1's children's children (as great-uncle/great-aunt)
          children.forEach((childData) => {
            const grandchildren = getPeopleInRelationship(
              childData.person.id,
              'CHILD',
              allRelationships,
              allPeople
            )
            grandchildren.forEach((gcData) => {
              const grandchild = gcData.person
              if (
                !relationshipExists(person2Id, grandchild.id, [
                  ...allRelationships,
                  ...newRelationships,
                  ...connections,
                ])
              ) {
                connections.push({
                  person_a_id: person2Id,
                  person_b_id: grandchild.id,
                  relationshipType: 'grandsibling_child',
                  explanation: `${person2.name} becomes great-aunt/uncle to ${grandchild.name}`,
                })
              }
            })
          })

          // 8. Connect person2 to person1's other siblings
          parents.forEach((parentData) => {
            const person1sSiblings = getPeopleInRelationship(
              parentData.person.id,
              'CHILD',
              allRelationships,
              allPeople
            ).filter(
              (sibData) =>
                sibData.person.id !== person1Id && sibData.person.id !== person2Id
            ) // Exclude person1 and person2
            person1sSiblings.forEach((sibData) => {
              const sibling = sibData.person
              if (
                !relationshipExists(person2Id, sibling.id, [
                  ...allRelationships,
                  ...newRelationships,
                  ...connections,
                ])
              ) {
                connections.push({
                  person_a_id: person2Id,
                  person_b_id: sibling.id,
                  relationshipType: 'sibling',
                  explanation: `${person2.name} becomes sibling to ${sibling.name}`,
                })
              }
            })
          })
          return connections
        }

        // Run connections for both people
        newRelationships.push(...processSiblingConnections(primaryPersonId, newPersonId))
        newRelationships.push(...processSiblingConnections(newPersonId, primaryPersonId))

        // Finally, connect all siblings together in one group
        const allSiblingsInGroup = new Set([primaryPersonId, newPersonId])
        getPeopleInRelationship(
          primaryPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        ).forEach((s) => allSiblingsInGroup.add(s.person.id))
        getPeopleInRelationship(
          newPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        ).forEach((s) => allSiblingsInGroup.add(s.person.id))

        const siblingArray = Array.from(allSiblingsInGroup)
        for (let i = 0; i < siblingArray.length; i++) {
          for (let j = i + 1; j < siblingArray.length; j++) {
            const p1Id = siblingArray[i]
            const p2Id = siblingArray[j]
            if (
              !relationshipExists(p1Id, p2Id, [
                ...allRelationships,
                ...newRelationships,
              ])
            ) {
              const p1 = allPeople.find((p) => p.id === p1Id)
              const p2 = allPeople.find((p) => p.id === p2Id)
              if (p1 && p2) {
                newRelationships.push({
                  person_a_id: p1Id,
                  person_b_id: p2Id,
                  relationshipType: 'sibling',
                  explanation: `${p1.name} and ${p2.name} become siblings`,
                })
              }
            }
          }
        }
      }

      // SIBLING-IN-LAW LOGIC - Create in-law connections
      else if (isRelationshipType(relationshipType, 'SIBLING_IN_LAW')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING SIBLING-IN-LAW RELATIONSHIP ---')
        // Get primary person's spouse
        const primarySpouses = getPeopleInRelationship(
          primaryPersonId,
          'SPOUSE',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${primarySpouses.length} spouse(s)`)

        // Make the sibling-in-law also a spouse to primary person's spouse
        primarySpouses.forEach((spouseData) => {
          const spousePerson = spouseData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, spousePerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} a spouse to ${spousePerson.name}`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: spousePerson.id,
              relationshipType: 'spouse',
              explanation: `${newPerson.name} becomes ${spousePerson.name}'s spouse`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${spousePerson.name}`
            )
          }
        })

        // Get primary person's siblings
        const primarySiblings = getPeopleInRelationship(
          primaryPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${primarySiblings.length} sibling(s)`)

        // Make the sibling-in-law also a sibling to primary person's siblings
        primarySiblings.forEach((siblingData) => {
          const siblingPerson = siblingData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, siblingPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} a sibling to ${siblingPerson.name}`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: siblingPerson.id,
              relationshipType: 'sibling',
              explanation: `${newPerson.name} becomes ${siblingPerson.name}'s sibling`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${siblingPerson.name}`
            )
          }
        })
      }

      // GRANDPARENT LOGIC - Create extended family connections
      else if (isRelationshipType(relationshipType, 'GRANDPARENT')) {
        console.log('\n👴👵 --- PROCESSING GRANDPARENT RELATIONSHIP ---')
        // Get the primary person's parents
        const primaryParents = getPeopleInRelationship(
          primaryPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${primaryParents.length} parent(s)`)

        // Connect the grandparent to the primary person's parents as a parent
        primaryParents.forEach((parentData) => {
          const parentPerson = parentData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, parentPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} a parent of ${parentPerson.name}`
            )
            newRelationships.push({
              person_a_id: newPersonId, // grandparent
              person_b_id: parentPerson.id, // parent
              relationshipType: 'child', // what parent is to grandparent
              explanation: `${newPerson.name} becomes ${parentPerson.name}'s parent`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${parentPerson.name}`
            )
          }
        })

        // Get the grandparent's other children (parent's siblings - aunts/uncles)
        const grandparentChildren = getPeopleInRelationship(
          newPersonId,
          'CHILD',
          allRelationships,
          allPeople
        ).filter(
          (childData) =>
            !primaryParents.some((parent) => parent.person.id === childData.person.id) // Exclude the primary person's parents
        )
        console.log(
          `${newPerson.name} has ${grandparentChildren.length} other child(ren)`
        )

        // Connect the grandparent's other children to the primary person as aunts/uncles
        grandparentChildren.forEach((childData) => {
          const auntOrUncle = childData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              primaryPersonId,
              auntOrUncle.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${auntOrUncle.name} an aunt/uncle to ${primaryPerson.name}`
            )
            newRelationships.push({
              person_a_id: auntOrUncle.id, // aunt/uncle
              person_b_id: primaryPersonId, // primary person
              relationshipType: 'sibling_child', // niece/nephew relationship to aunt/uncle
              explanation: `${auntOrUncle.name} becomes ${primaryPerson.name}'s aunt/uncle`,
            })
          } else {
            console.log(
              `⚠️ ${auntOrUncle.name} is already connected to ${primaryPerson.name}`
            )
          }
        })

        // Connect the grandparent's other children to the primary person's siblings as aunts/uncles
        const primarySiblings = getPeopleInRelationship(
          primaryPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        )
        console.log(
          `${primaryPerson.name} has ${primarySiblings.length} sibling(s)`
        )
        primarySiblings.forEach((siblingData) => {
          const siblingPerson = siblingData.person
          grandparentChildren.forEach((childData) => {
            const auntOrUncle = childData.person
            // CRITICAL: Check if relationship already exists
            if (
              !relationshipExists(
                siblingPerson.id,
                auntOrUncle.id,
                allRelationships
              )
            ) {
              console.log(
                `➕ Making ${auntOrUncle.name} an aunt/uncle to ${siblingPerson.name}`
              )
              newRelationships.push({
                person_a_id: auntOrUncle.id, // aunt/uncle
                person_b_id: siblingPerson.id, // sibling
                relationshipType: 'sibling_child', // niece/nephew relationship to aunt/uncle
                explanation: `${auntOrUncle.name} becomes ${siblingPerson.name}'s aunt/uncle`,
              })
            } else {
              console.log(
                `⚠️ ${auntOrUncle.name} is already connected to ${siblingPerson.name}`
              )
            }
          })
        })

        // Connect with grandparent's parents (great-grandparents)
        const greatGrandParents = getPeopleInRelationship(
          newPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(
          `${newPerson.name} has ${greatGrandParents.length} parent(s) (great-grandparents to primary person)`
        )
        greatGrandParents.forEach((greatGrandParentData) => {
          const greatGrandParent = greatGrandParentData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              primaryPersonId,
              greatGrandParent.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${greatGrandParent.name} a great-grandparent to ${primaryPerson.name}`
            )
            newRelationships.push({
              person_a_id: greatGrandParent.id,
              person_b_id: primaryPersonId,
              relationshipType: 'great_grandchild',
              explanation: `${greatGrandParent.name} becomes ${primaryPerson.name}'s great-grandparent`,
            })
          } else {
            console.log(
              `⚠️ ${greatGrandParent.name} is already connected to ${primaryPerson.name}`
            )
          }
        })

        // DEEPER GENERATION CONNECTIONS
        // Connect primary person's children to the grandparent as great-grandchildren
        const primaryChildren = getPeopleInRelationship(
          primaryPersonId,
          'CHILD',
          allRelationships,
          allPeople
        )
        console.log(
          `${primaryPerson.name} has ${primaryChildren.length} child(ren) (great-grandchildren to ${newPerson.name})`
        )
        primaryChildren.forEach((childData) => {
          const child = childData.person
          // CRITICAL: Check if relationship already exists
          if (!relationshipExists(newPersonId, child.id, allRelationships)) {
            console.log(
              `➕ Making ${child.name} a great-grandchild to ${newPerson.name}`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: child.id,
              relationshipType: 'great_grandchild',
              explanation: `${child.name} becomes ${newPerson.name}'s great-grandchild`,
            })
          } else {
            console.log(
              `⚠️ ${child.name} is already connected to ${newPerson.name}`
            )
          }
        })

        // Connect primary person's siblings' children to the grandparent as grandchildren
        primarySiblings.forEach((siblingData) => {
          const sibling = siblingData.person
          const siblingChildren = getPeopleInRelationship(
            sibling.id,
            'CHILD',
            allRelationships,
            allPeople
          )
          console.log(
            `${sibling.name} has ${siblingChildren.length} child(ren) (grandchildren to ${newPerson.name})`
          )
          siblingChildren.forEach((childData) => {
            const child = childData.person
            // CRITICAL: Check if relationship already exists
            if (!relationshipExists(newPersonId, child.id, allRelationships)) {
              console.log(
                `➕ Making ${child.name} a grandchild to ${newPerson.name}`
              )
              newRelationships.push({
                person_a_id: newPersonId,
                person_b_id: child.id,
                relationshipType: 'grandchild',
                explanation: `${child.name} becomes ${newPerson.name}'s grandchild`,
              })
            } else {
              console.log(
                `⚠️ ${child.name} is already connected to ${newPerson.name}`
              )
            }
          })
        })
      }

      // UNCLE/AUNT LOGIC - Create cousin relationships
      else if (isRelationshipType(relationshipType, 'PARENTAL_SIBLING')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING UNCLE/AUNT RELATIONSHIP ---')
        // Get the primary person's parents
        const primaryParents = getPeopleInRelationship(
          primaryPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${primaryParents.length} parent(s)`)

        // Connect the uncle/aunt to the primary person's parents as a sibling
        primaryParents.forEach((parentData) => {
          const parentPerson = parentData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, parentPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} a sibling of ${parentPerson.name}`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: parentPerson.id,
              relationshipType: 'sibling',
              explanation: `${newPerson.name} becomes ${parentPerson.name}'s sibling`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${parentPerson.name}`
            )
          }
        })

        // Get the uncle/aunt's children (the primary person's cousins)
        const cousins = getPeopleInRelationship(
          newPersonId,
          'CHILD',
          allRelationships,
          allPeople
        )
        console.log(`${newPerson.name} has ${cousins.length} child(ren)`)

        // Connect the cousins to the primary person as cousins
        cousins.forEach((cousinData) => {
          const cousinPerson = cousinData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              primaryPersonId,
              cousinPerson.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${cousinPerson.name} and ${primaryPerson.name} cousins`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: cousinPerson.id,
              relationshipType: 'cousin',
              explanation: `${primaryPerson.name} and ${cousinPerson.name} become first cousins`,
            })
          } else {
            console.log(
              `⚠️ ${primaryPerson.name} and ${cousinPerson.name} are already connected`
            )
          }
        })

        // Get the primary person's siblings
        const primarySiblings = getPeopleInRelationship(
          primaryPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        )
        console.log(
          `${primaryPerson.name} has ${primarySiblings.length} sibling(s)`
        )
        // Connect the uncle/aunt to the primary person's siblings
        primarySiblings.forEach((siblingData) => {
          const siblingPerson = siblingData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, siblingPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} a sibling of ${siblingPerson.name}`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: siblingPerson.id,
              relationshipType: 'sibling',
              explanation: `${newPerson.name} becomes ${siblingPerson.name}'s sibling`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${siblingPerson.name}`
            )
          }
        })

        // Connect the primary person's siblings' children to the uncle/aunt as cousins
        primarySiblings.forEach((siblingData) => {
          const siblingPerson = siblingData.person
          const siblingChildren = getPeopleInRelationship(
            siblingPerson.id,
            'CHILD',
            allRelationships,
            allPeople
          )
          console.log(
            `${siblingPerson.name} has ${siblingChildren.length} child(ren)`
          )
          siblingChildren.forEach((childData) => {
            const childPerson = childData.person
            // CRITICAL: Check if relationship already exists
            if (
              !relationshipExists(newPersonId, childPerson.id, allRelationships)
            ) {
              console.log(
                `➕ Making ${newPerson.name} and ${childPerson.name} cousins`
              )
              newRelationships.push({
                person_a_id: newPersonId,
                person_b_id: childPerson.id,
                relationshipType: 'cousin',
                explanation: `${newPerson.name} and ${childPerson.name} become first cousins`,
              })
            } else {
              console.log(
                `⚠️ ${newPerson.name} and ${childPerson.name} are already connected`
              )
            }
          })
        })

        // Connect with uncle/aunt's parents (great-aunts/great-uncles to primary person)
        const grandParents = getPeopleInRelationship(
          newPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(
          `${newPerson.name} has ${grandParents.length} parent(s) (great-aunts/great-uncles to primary person)`
        )
        grandParents.forEach((grandParentData) => {
          const greatAuntUncle = grandParentData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              primaryPersonId,
              greatAuntUncle.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${greatAuntUncle.name} a great-aunt/uncle to ${primaryPerson.name}`
            )
            newRelationships.push({
              person_a_id: greatAuntUncle.id,
              person_b_id: primaryPersonId,
              relationshipType: 'grandsibling_child',
              explanation: `${greatAuntUncle.name} becomes ${primaryPerson.name}'s great-aunt/uncle`,
            })
          } else {
            console.log(
              `⚠️ ${greatAuntUncle.name} is already connected to ${primaryPerson.name}`
            )
          }
        })

        // DEEPER GENERATION CONNECTIONS
        // Connect uncle/aunt's grandchildren to primary person as second cousins
        cousins.forEach((cousinData) => {
          const cousin = cousinData.person
          const cousinChildren = getPeopleInRelationship(
            cousin.id,
            'CHILD',
            allRelationships,
            allPeople
          )
          console.log(
            `${cousin.name} has ${cousinChildren.length} child(ren) (second cousins to ${primaryPerson.name})`
          )
          cousinChildren.forEach((childData) => {
            const secondCousin = childData.person
            // CRITICAL: Check if relationship already exists
            if (
              !relationshipExists(
                primaryPersonId,
                secondCousin.id,
                allRelationships
              )
            ) {
              console.log(
                `➕ Making ${primaryPerson.name} and ${secondCousin.name} second cousins`
              )
              newRelationships.push({
                person_a_id: primaryPersonId,
                person_b_id: secondCousin.id,
                relationshipType: 'second_cousin',
                explanation: `${primaryPerson.name} and ${secondCousin.name} become second cousins`,
              })
            } else {
              console.log(
                `⚠️ ${primaryPerson.name} and ${secondCousin.name} are already connected`
              )
            }
          })
        })
      }

      // NIECE/NEPHEW LOGIC - Create extended family connections
      else if (isRelationshipType(relationshipType, 'SIBLING_CHILD')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING NIECE/NEPHEW RELATIONSHIP ---')
        // primaryPerson is the uncle/aunt, newPerson is the niece/nephew

        // Connect niece/nephew's children to primary person as grandnieces/grandnephews
        const grandChildren = getPeopleInRelationship(
          newPersonId, // The niece/nephew
          'CHILD',
          allRelationships,
          allPeople
        )
        console.log(
          `${newPerson.name} has ${grandChildren.length} child(ren) (grandnieces/nephews to ${primaryPerson.name})`
        )
        grandChildren.forEach((childData) => {
          const grandNieceNephew = childData.person
          if (
            !relationshipExists(
              primaryPersonId,
              grandNieceNephew.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${grandNieceNephew.name} a grandniece/nephew to ${primaryPerson.name}`
            )
            newRelationships.push({
              person_a_id: primaryPersonId, // The great-aunt/uncle
              person_b_id: grandNieceNephew.id, // The grandniece/nephew
              relationshipType: 'grandsibling_child',
              explanation: `${grandNieceNephew.name} becomes ${primaryPerson.name}'s grandniece/nephew`,
            })
          }
        })

        // Connect primary person's children (cousins) to the new niece/nephew
        const cousins = getPeopleInRelationship(
          primaryPersonId, // The uncle/aunt
          'CHILD',
          allRelationships,
          allPeople
        )
        console.log(
          `${primaryPerson.name} has ${cousins.length} child(ren) (cousins to ${newPerson.name})`
        )
        cousins.forEach((cousinData) => {
          const cousin = cousinData.person
          if (!relationshipExists(newPersonId, cousin.id, allRelationships)) {
            console.log(
              `➕ Making ${cousin.name} and ${newPerson.name} cousins`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: cousin.id,
              relationshipType: 'cousin',
              explanation: `${newPerson.name} and ${cousin.name} become first cousins`,
            })
          }
        })

        // Connect primary person's parents (grandparents) to the new niece/nephew
        const grandParents = getPeopleInRelationship(
          primaryPersonId, // The uncle/aunt
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(
          `${primaryPerson.name} has ${grandParents.length} parent(s) (grandparents to ${newPerson.name})`
        )
        grandParents.forEach((parentData) => {
          const grandParent = parentData.person
          if (!relationshipExists(newPersonId, grandParent.id, allRelationships)) {
            console.log(
              `➕ Making ${grandParent.name} a grandparent to ${newPerson.name}`
            )
            newRelationships.push({
              person_a_id: grandParent.id,
              person_b_id: newPersonId,
              relationshipType: 'grandchild',
              explanation: `${grandParent.name} becomes ${newPerson.name}'s grandparent`,
            })
          }
        })
      }

      // COUSIN LOGIC - Create extended family connections
      else if (isRelationshipType(relationshipType, 'COUSIN')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING COUSIN RELATIONSHIP ---')
        // Get the primary person's parents
        const primaryParents = getPeopleInRelationship(
          primaryPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${primaryParents.length} parent(s)`)

        // Get the new person's parents
        const newPersonParents = getPeopleInRelationship(
          newPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(`${newPerson.name} has ${newPersonParents.length} parent(s)`)

        // Connect the parents as siblings (if not already connected)
        primaryParents.forEach((primaryParent) => {
          newPersonParents.forEach((newParent) => {
            // CRITICAL: Check if relationship already exists
            if (
              !relationshipExists(
                primaryParent.person.id,
                newParent.person.id,
                allRelationships
              )
            ) {
              console.log(
                `➕ Making ${primaryParent.person.name} and ${newParent.person.name} siblings`
              )
              newRelationships.push({
                person_a_id: primaryParent.person.id,
                person_b_id: newParent.person.id,
                relationshipType: 'sibling',
                explanation: `${primaryParent.person.name} and ${newParent.person.name} become siblings`,
              })
            } else {
              console.log(
                `⚠️ ${primaryParent.person.name} and ${newParent.person.name} are already connected`
              )
            }
          })
        })

        // Get the primary person's siblings
        const primarySiblings = getPeopleInRelationship(
          primaryPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        )
        console.log(
          `${primaryPerson.name} has ${primarySiblings.length} sibling(s)`
        )

        // Connect the new cousin to the primary person's siblings as cousins
        primarySiblings.forEach((siblingData) => {
          const siblingPerson = siblingData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, siblingPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} and ${siblingPerson.name} cousins`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: siblingPerson.id,
              relationshipType: 'cousin',
              explanation: `${newPerson.name} and ${siblingPerson.name} become first cousins`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${siblingPerson.name}`
            )
          }
        })

        // Get the new person's siblings
        const newPersonSiblings = getPeopleInRelationship(
          newPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        )
        console.log(`${newPerson.name} has ${newPersonSiblings.length} sibling(s)`)

        // Connect the primary person to the new person's siblings as cousins
        newPersonSiblings.forEach((siblingData) => {
          const siblingPerson = siblingData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              primaryPersonId,
              siblingPerson.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${primaryPerson.name} and ${siblingPerson.name} cousins`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: siblingPerson.id,
              relationshipType: 'cousin',
              explanation: `${primaryPerson.name} and ${siblingPerson.name} become first cousins`,
            })
          } else {
            console.log(
              `⚠️ ${primaryPerson.name} and ${siblingPerson.name} are already connected`
            )
          }
        })

        // Connect the new person's children to the primary person as first cousins once removed
        const newPersonChildren = getPeopleInRelationship(
          newPersonId,
          'CHILD',
          allRelationships,
          allPeople
        )
        console.log(
          `${newPerson.name} has ${newPersonChildren.length} child(ren)`
        )
        newPersonChildren.forEach((childData) => {
          const childPerson = childData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              primaryPersonId,
              childPerson.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${childPerson.name} and ${primaryPerson.name} first cousins once removed`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: childPerson.id,
              relationshipType: 'first_cousin_once_removed',
              explanation: `${primaryPerson.name} and ${childPerson.name} become first cousins once removed`,
            })
          } else {
            console.log(
              `⚠️ ${primaryPerson.name} and ${childPerson.name} are already connected`
            )
          }
        })

        // Connect the primary person's children to the new person as first cousins once removed
        const primaryChildren = getPeopleInRelationship(
          primaryPersonId,
          'CHILD',
          allRelationships,
          allPeople
        )
        console.log(
          `${primaryPerson.name} has ${primaryChildren.length} child(ren)`
        )
        primaryChildren.forEach((childData) => {
          const childPerson = childData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, childPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${childPerson.name} and ${newPerson.name} first cousins once removed`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: childPerson.id,
              relationshipType: 'first_cousin_once_removed',
              explanation: `${newPerson.name} and ${childPerson.name} become first cousins once removed`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} and ${childPerson.name} are already connected`
            )
          }
        })

        // DEEPER GENERATION CONNECTIONS
        // Connect the new person's grandchildren to the primary person as second cousins
        newPersonChildren.forEach((childData) => {
          const child = childData.person
          const grandchildren = getPeopleInRelationship(
            child.id,
            'CHILD',
            allRelationships,
            allPeople
          )
          console.log(
            `${child.name} has ${grandchildren.length} grandchild(ren)`
          )
          grandchildren.forEach((gcData) => {
            const grandchild = gcData.person
            // CRITICAL: Check if relationship already exists
            if (
              !relationshipExists(
                primaryPersonId,
                grandchild.id,
                allRelationships
              )
            ) {
              console.log(
                `➕ Making ${primaryPerson.name} and ${grandchild.name} second cousins`
              )
              newRelationships.push({
                person_a_id: primaryPersonId,
                person_b_id: grandchild.id,
                relationshipType: 'second_cousin',
                explanation: `${primaryPerson.name} and ${grandchild.name} become second cousins`,
              })
            } else {
              console.log(
                `⚠️ ${primaryPerson.name} and ${grandchild.name} are already connected`
              )
            }
          })
        })

        // Connect the primary person's grandchildren to the new person as second cousins
        primaryChildren.forEach((childData) => {
          const child = childData.person
          const grandchildren = getPeopleInRelationship(
            child.id,
            'CHILD',
            allRelationships,
            allPeople
          )
          console.log(
            `${child.name} has ${grandchildren.length} grandchild(ren)`
          )
          grandchildren.forEach((gcData) => {
            const grandchild = gcData.person
            // CRITICAL: Check if relationship already exists
            if (
              !relationshipExists(newPersonId, grandchild.id, allRelationships)
            ) {
              console.log(
                `➕ Making ${newPerson.name} and ${grandchild.name} second cousins`
              )
              newRelationships.push({
                person_a_id: newPersonId,
                person_b_id: grandchild.id,
                relationshipType: 'second_cousin',
                explanation: `${newPerson.name} and ${grandchild.name} become second cousins`,
              })
            } else {
              console.log(
                `⚠️ ${newPerson.name} and ${grandchild.name} are already connected`
              )
            }
          })
        })
      }

      // GRANDCHILD LOGIC - Create extended family connections
      else if (isRelationshipType(relationshipType, 'GRANDCHILD')) {
        console.log('\n👶 --- PROCESSING GRANDCHILD RELATIONSHIP ---')
        // Get the primary person's children
        const primaryChildren = getPeopleInRelationship(
          primaryPersonId,
          'CHILD',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${primaryChildren.length} child(ren)`)

        // Connect the grandchild to the primary person's children as a parent
        primaryChildren.forEach((childData) => {
          const childPerson = childData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, childPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${childPerson.name} a parent of ${newPerson.name}`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: childPerson.id,
              relationshipType: 'parent',
              explanation: `${childPerson.name} becomes ${newPerson.name}'s parent`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${childPerson.name}`
            )
          }
        })

        // Connect the grandchild to the primary person's other grandchildren as siblings
        const otherGrandchildren = getPeopleInRelationship(
          primaryPersonId,
          'GRANDCHILD',
          allRelationships,
          allPeople
        ).filter((gcData) => gcData.person.id !== newPersonId)
        console.log(
          `${primaryPerson.name} has ${otherGrandchildren.length} other grandchild(ren)`
        )
        otherGrandchildren.forEach((grandchildData) => {
          const otherGrandchild = grandchildData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              newPersonId,
              otherGrandchild.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${newPerson.name} and ${otherGrandchild.name} siblings`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: otherGrandchild.id,
              relationshipType: 'sibling',
              explanation: `${newPerson.name} and ${otherGrandchild.name} become siblings`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} and ${otherGrandchild.name} are already connected`
            )
          }
        })

        // Connect with primary person's siblings (great-aunts/great-uncles to grandchild)
        const primarySiblings = getPeopleInRelationship(
          primaryPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${primarySiblings.length} sibling(s)`)
        primarySiblings.forEach((siblingData) => {
          const greatAuntUncle = siblingData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, greatAuntUncle.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${greatAuntUncle.name} a great-aunt/uncle to ${newPerson.name}`
            )
            newRelationships.push({
              person_a_id: greatAuntUncle.id,
              person_b_id: newPersonId,
              relationshipType: 'grandsibling_child',
              explanation: `${greatAuntUncle.name} becomes ${newPerson.name}'s great-aunt/uncle`,
            })
          } else {
            console.log(
              `⚠️ ${greatAuntUncle.name} is already connected to ${newPerson.name}`
            )
          }
        })

        // Connect with primary person's parents (great-great-grandparents to grandchild)
        const greatGrandParents = getPeopleInRelationship(
          primaryPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${greatGrandParents.length} parent(s)`)
        greatGrandParents.forEach((greatGrandParentData) => {
          const greatGreatGrandParent = greatGrandParentData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              newPersonId,
              greatGreatGrandParent.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${greatGreatGrandParent.name} a great-great-grandparent to ${newPerson.name}`
            )
            newRelationships.push({
              person_a_id: greatGreatGrandParent.id,
              person_b_id: newPersonId,
              relationshipType: 'great_great_grandchild',
              explanation: `${greatGreatGrandParent.name} becomes ${newPerson.name}'s great-great-grandparent`,
            })
          } else {
            console.log(
              `⚠️ ${greatGreatGrandParent.name} is already connected to ${newPerson.name}`
            )
          }
        })

        // DEEPER GENERATION CONNECTIONS
        // Connect the grandchild's children to the primary person as great-great-grandchildren
        const grandchildChildren = getPeopleInRelationship(
          newPersonId,
          'CHILD',
          allRelationships,
          allPeople
        )
        console.log(
          `${newPerson.name} has ${grandchildChildren.length} child(ren) (great-great-grandchildren to ${primaryPerson.name})`
        )
        grandchildChildren.forEach((childData) => {
          const child = childData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(primaryPersonId, child.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${child.name} a great-great-grandchild to ${primaryPerson.name}`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: child.id,
              relationshipType: 'great_great_grandchild',
              explanation: `${child.name} becomes ${primaryPerson.name}'s great-great-grandchild`,
            })
          } else {
            console.log(
              `⚠️ ${child.name} is already connected to ${primaryPerson.name}`
            )
          }
        })

        // Connect the grandchild's siblings to the primary person as grandchildren
        const grandchildSiblings = getPeopleInRelationship(
          newPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        )
        console.log(
          `${newPerson.name} has ${grandchildSiblings.length} sibling(s) (grandchildren to ${primaryPerson.name})`
        )
        grandchildSiblings.forEach((siblingData) => {
          const sibling = siblingData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(primaryPersonId, sibling.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${sibling.name} a grandchild to ${primaryPerson.name}`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: sibling.id,
              relationshipType: 'grandchild',
              explanation: `${sibling.name} becomes ${primaryPerson.name}'s grandchild`,
            })
          } else {
            console.log(
              `⚠️ ${sibling.name} is already connected to ${primaryPerson.name}`
            )
          }
        })
      }

      // PARTNER LOGIC - Similar to spouse but without family connections
      else if (isRelationshipType(relationshipType, 'PARTNER')) {
        console.log('\n👥 --- PROCESSING PARTNER RELATIONSHIP ---')
        // Partners don't automatically create family connections like spouses do
        // But they are symmetric relationships
        console.log(
          `🤝 ${primaryPerson.name} and ${newPerson.name} are now partners`
        )
      }

      // SECOND COUSIN LOGIC - Create extended family connections
      else if (isRelationshipType(relationshipType, 'SECOND_COUSIN')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING 2ND COUSIN RELATIONSHIP ---')
        console.log(
          `👨‍👩‍👧‍👦 ${primaryPerson.name} and ${newPerson.name} are now second cousins`
        )
        // Connect second cousins' children as third cousins
        const primaryChildren = getPeopleInRelationship(
          primaryPersonId,
          'CHILD',
          allRelationships,
          allPeople
        )
        const newPersonChildren = getPeopleInRelationship(
          newPersonId,
          'CHILD',
          allRelationships,
          allPeople
        )

        primaryChildren.forEach((primaryChildData) => {
          const primaryChild = primaryChildData.person
          newPersonChildren.forEach((newPersonChildData) => {
            const newPersonChild = newPersonChildData.person
            // CRITICAL: Check if relationship already exists
            if (
              !relationshipExists(
                primaryChild.id,
                newPersonChild.id,
                allRelationships
              )
            ) {
              console.log(
                `➕ Making ${primaryChild.name} and ${newPersonChild.name} third cousins`
              )
              newRelationships.push({
                person_a_id: primaryChild.id,
                person_b_id: newPersonChild.id,
                relationshipType: 'third_cousin',
                explanation: `${primaryChild.name} and ${newPersonChild.name} become third cousins`,
              })
            } else {
              console.log(
                `⚠️ ${primaryChild.name} and ${newPersonChild.name} are already connected`
              )
            }
          })
        })
      }

      // THIRD COUSIN LOGIC - Create extended family connections
      else if (isRelationshipType(relationshipType, 'THIRD_COUSIN')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING 3RD COUSIN RELATIONSHIP ---')
        console.log(
          `👨‍👩‍👧‍👦 ${primaryPerson.name} and ${newPerson.name} are now third cousins`
        )
      }

      // STEP PARENT LOGIC
      else if (isRelationshipType(relationshipType, 'STEP_PARENT')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING STEP PARENT RELATIONSHIP ---')
        // Connect step parent to step child
        // Step parent's spouse (if exists) becomes step parent to step child as well
        const stepParentSpouses = getPeopleInRelationship(
          newPersonId,
          'SPOUSE',
          allRelationships,
          allPeople
        )
        stepParentSpouses.forEach((spouseData) => {
          const spousePerson = spouseData.person
          if (
            !relationshipExists(
              spousePerson.id,
              primaryPersonId,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${spousePerson.name} a step parent to ${primaryPerson.name}`
            )
            newRelationships.push({
              person_a_id: spousePerson.id,
              person_b_id: primaryPersonId,
              relationshipType: 'step_child',
              explanation: `${spousePerson.name} becomes ${primaryPerson.name}'s step parent`,
            })
          }
        })
      }

      // STEP CHILD LOGIC
      else if (isRelationshipType(relationshipType, 'STEP_CHILD')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING STEP CHILD RELATIONSHIP ---')
        // Connect step child to step parent
        // Step child's other parent (if exists) becomes step child to step parent as well
        const stepChildParents = getPeopleInRelationship(
          newPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        stepChildParents.forEach((parentData) => {
          const parentPerson = parentData.person
          if (
            !relationshipExists(
              primaryPersonId,
              parentPerson.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${primaryPerson.name} a step child to ${parentPerson.name}`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: parentPerson.id,
              relationshipType: 'step_parent',
              explanation: `${primaryPerson.name} becomes ${parentPerson.name}'s step child`,
            })
          }
        })
      }

      // HALF SIBLING LOGIC
      else if (isRelationshipType(relationshipType, 'HALF_SIBLING')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING HALF SIBLING RELATIONSHIP ---')
        // Connect half siblings to their shared parent
        const sharedParent1 = getPeopleInRelationship(
          primaryPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        const sharedParent2 = getPeopleInRelationship(
          newPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        // Find common parent
        const commonParents = sharedParent1.filter((p1) =>
          sharedParent2.some((p2) => p1.person.id === p2.person.id)
        )
        if (commonParents.length > 0) {
          console.log(
            `${primaryPerson.name} and ${newPerson.name} share ${commonParents.length} parent(s)`
          )
        } else {
          console.log(
            `${primaryPerson.name} and ${newPerson.name} don't have a common parent`
          )
        }
      }

      // ADOPTIVE PARENT LOGIC
      else if (isRelationshipType(relationshipType, 'ADOPTIVE_PARENT')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING ADOPTIVE PARENT RELATIONSHIP ---')
        // Connect adoptive parent to adopted child
        // Adopted child's other parent (if exists) becomes adopted child to adoptive parent as well
        const adoptedChildParents = getPeopleInRelationship(
          newPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        adoptedChildParents.forEach((parentData) => {
          const parentPerson = parentData.person
          if (
            !relationshipExists(
              primaryPersonId,
              parentPerson.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${primaryPerson.name} an adoptive parent to ${parentPerson.name}`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: parentPerson.id,
              relationshipType: 'adoptive_child',
              explanation: `${primaryPerson.name} becomes ${parentPerson.name}'s adoptive parent`,
            })
          }
        })
      }

      // ADOPTIVE CHILD LOGIC
      else if (isRelationshipType(relationshipType, 'ADOPTIVE_CHILD')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING ADOPTIVE CHILD RELATIONSHIP ---')
        // Connect adoptive child to adoptive parent
        // Adoptive parent's other child (if exists) becomes adoptive child to adoptive child as well
        const adoptiveParentChildren = getPeopleInRelationship(
          newPersonId,
          'CHILD',
          allRelationships,
          allPeople
        )
        adoptiveParentChildren.forEach((childData) => {
          const childPerson = childData.person
          if (
            !relationshipExists(
              primaryPersonId,
              childPerson.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${primaryPerson.name} an adoptive child to ${childPerson.name}`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: childPerson.id,
              relationshipType: 'adoptive_parent',
              explanation: `${primaryPerson.name} becomes ${childPerson.name}'s adoptive child`,
            })
          }
        })
      }

      // CHILD-IN-LAW LOGIC - Create extended family connections
      else if (isRelationshipType(relationshipType, 'CHILD_IN_LAW')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING CHILD-IN-LAW RELATIONSHIP ---')
        // Get the parent person's children (spouses of the child-in-law)
        const parentChildren = getPeopleInRelationship(
          primaryPersonId,
          'CHILD',
          allRelationships,
          allPeople
        )
        console.log(
          `${primaryPerson.name} has ${parentChildren.length} child(ren)`
        )

        // Connect child-in-law to parent's other children as sibling-in-law
        parentChildren.forEach((childData) => {
          const childPerson = childData.person
          // Skip the spouse connection (already exists)
          if (childPerson.id === newPersonId) return
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, childPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} a sibling-in-law to ${childPerson.name}`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: childPerson.id,
              relationshipType: 'sibling_in_law',
              explanation: `${newPerson.name} becomes ${childPerson.name}'s sibling-in-law`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${childPerson.name}`
            )
          }
        })

        // Get the child-in-law's parents
        const childInLawParents = getPeopleInRelationship(
          newPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(
          `${newPerson.name} has ${childInLawParents.length} parent(s)`
        )

        // Connect parent to child-in-law's other parent as sibling-in-law
        childInLawParents.forEach((parentData) => {
          const parentOfChildInLaw = parentData.person
          // Skip the connection to themselves
          if (parentOfChildInLaw.id === primaryPersonId) return
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              primaryPersonId,
              parentOfChildInLaw.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${primaryPerson.name} a sibling-in-law to ${parentOfChildInLaw.name}`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: parentOfChildInLaw.id,
              relationshipType: 'sibling_in_law',
              explanation: `${primaryPerson.name} becomes ${parentOfChildInLaw.name}'s sibling-in-law`,
            })
          } else {
            console.log(
              `⚠️ ${primaryPerson.name} is already connected to ${parentOfChildInLaw.name}`
            )
          }
        })
      }

      // PARENT-IN-LAW LOGIC - Create extended family connections
      else if (isRelationshipType(relationshipType, 'PARENT_IN_LAW')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING PARENT-IN-LAW RELATIONSHIP ---')
        // Get the child person's spouse(s)
        const childSpouses = getPeopleInRelationship(
          primaryPersonId,
          'SPOUSE',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${childSpouses.length} spouse(s)`)

        // Connect parent-in-law to child's spouse as parent-in-law
        childSpouses.forEach((spouseData) => {
          const spousePerson = spouseData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, spousePerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} a parent-in-law to ${spousePerson.name}`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: spousePerson.id,
              relationshipType: 'child_in_law',
              explanation: `${newPerson.name} becomes ${spousePerson.name}'s parent-in-law`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${spousePerson.name}`
            )
          }
        })
      }

      // NIECE/NEPHEW-IN-LAW LOGIC - Create extended family connections
      else if (isRelationshipType(relationshipType, 'SIBLING_CHILD_IN_LAW')) {
        console.log('\n👨‍👩‍👧‍👦 --- PROCESSING NIECE/NEPHEW-IN-LAW RELATIONSHIP ---')
        // Get the person's siblings
        const siblings = getPeopleInRelationship(
          primaryPersonId,
          'SIBLING',
          allRelationships,
          allPeople
        )
        console.log(`${primaryPerson.name} has ${siblings.length} sibling(s)`)

        // Connect niece/nephew-in-law to person's siblings as spouse
        siblings.forEach((siblingData) => {
          const siblingPerson = siblingData.person
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(newPersonId, siblingPerson.id, allRelationships)
          ) {
            console.log(
              `➕ Making ${newPerson.name} a spouse to ${siblingPerson.name}`
            )
            newRelationships.push({
              person_a_id: newPersonId,
              person_b_id: siblingPerson.id,
              relationshipType: 'spouse',
              explanation: `${newPerson.name} becomes ${siblingPerson.name}'s spouse`,
            })
          } else {
            console.log(
              `⚠️ ${newPerson.name} is already connected to ${siblingPerson.name}`
            )
          }
        })

        // Get the niece/nephew-in-law's parents (the siblings)
        const niecesNephewsParents = getPeopleInRelationship(
          newPersonId,
          'PARENT',
          allRelationships,
          allPeople
        )
        console.log(
          `${newPerson.name} has ${niecesNephewsParents.length} parent(s)`
        )

        // Connect person to niece/nephew-in-law's parents as sibling-in-law
        niecesNephewsParents.forEach((parentData) => {
          const parentOfNieceNephew = parentData.person
          // Skip the connection to themselves
          if (parentOfNieceNephew.id === primaryPersonId) return
          // CRITICAL: Check if relationship already exists
          if (
            !relationshipExists(
              primaryPersonId,
              parentOfNieceNephew.id,
              allRelationships
            )
          ) {
            console.log(
              `➕ Making ${primaryPerson.name} a sibling-in-law to ${parentOfNieceNephew.name}`
            )
            newRelationships.push({
              person_a_id: primaryPersonId,
              person_b_id: parentOfNieceNephew.id,
              relationshipType: 'sibling_in_law',
              explanation: `${primaryPerson.name} becomes ${parentOfNieceNephew.name}'s sibling-in-law`,
            })
          } else {
            console.log(
              `⚠️ ${primaryPerson.name} is already connected to ${parentOfNieceNephew.name}`
            )
          }
        })
      }

      console.log(
        `\n✅===GENERATED ${newRelationships.length} SMART RELATIONSHIPS===`
      )
      newRelationships.forEach((rel, index) => {
        console.log(`${index + 1}. ${rel.explanation}`)
      })

      // Remove duplicates from the final list
      const uniqueRelationships = []
      const seen = new Set()
      for (const rel of newRelationships) {
        const key1 = `${rel.person_a_id}-${rel.person_b_id}`
        const key2 = `${rel.person_b_id}-${rel.person_a_id}`
        if (!seen.has(key1) && !seen.has(key2)) {
          uniqueRelationships.push(rel)
          seen.add(key1)
        }
      }

      return uniqueRelationships
    }

    // IMPROVED: Check if a relationship already exists between two people
    // This is the key function for preventing duplicates
    export const relationshipExists = (person1Id, person2Id, allRelationships) => {
      // Make sure we check both directions of the relationship
      const exists = allRelationships.some(
        (rel) =>
          (rel.person_a_id === person1Id && rel.person_b_id === person2Id) ||
          (rel.person_a_id === person2Id && rel.person_b_id === person1Id)
      )

      if (exists) {
        console.log(
          `⚠️ Relationship already exists between ${person1Id} and ${person2Id}`
        )
      }

      return exists
    }

    // Get relationship summary for display
    export const getRelationshipSummary = (newRelationships) => {
      if (newRelationships.length === 0) return null

      const summary = {
        count: newRelationships.length,
        types: {
          parent: 0,
          child: 0,
          sibling: 0,
          spouse: 0,
          partner: 0,
          cousin: 0,
          second_cousin: 0,
          third_cousin: 0,
          parental_sibling: 0, // uncle/aunt
          sibling_in_law: 0, // brother-in-law/sister-in-law
          parent_in_law: 0, // father-in-law/mother-in-law
          child_in_law: 0, // son-in-law/daughter-in-law
          grandparent: 0,
          grandchild: 0,
          great_grandparent: 0,
          great_grandchild: 0,
          great_great_grandparent: 0,
          grandsibling_child: 0, // grandniece/grandnephew
          grandparent_sibling: 0, // great-aunt/great-uncle
          first_cousin_once_removed: 0,
          second_cousin_once_removed: 0,
          first_cousin_twice_removed: 0,
          step_parent: 0,
          step_child: 0,
          step_sibling: 0,
          half_sibling: 0,
          adoptive_parent: 0,
          adoptive_child: 0,
          cousin_in_law: 0,
        },
        explanations: newRelationships.map((rel) => rel.explanation),
      }

      newRelationships.forEach((rel) => {
        if (rel.explanation.includes('child')) {
          if (rel.explanation.includes('grandchild')) {
            if (rel.explanation.includes('great-grandchild')) {
              summary.types.great_grandchild++
            } else {
              summary.types.grandchild++
            }
          } else if (rel.explanation.includes('great-great-grandchild')) {
            summary.types.great_great_grandparent++
          } else {
            summary.types.child++
          }
        } else if (rel.explanation.includes('parent')) {
          if (rel.explanation.includes('grandparent')) {
            if (rel.explanation.includes('great-grandparent')) {
              summary.types.great_grandparent++
            } else if (rel.explanation.includes('great-great-grandparent')) {
              summary.types.great_great_grandparent++
            } else {
              summary.types.grandparent++
            }
          } else {
            summary.types.parent++
          }
        } else if (rel.explanation.includes('spouse')) {
          summary.types.spouse++
        } else if (rel.explanation.includes('partner')) {
          summary.types.partner++
        } else if (
          rel.explanation.includes('cousin') &&
          rel.explanation.includes('third')
        ) {
          summary.types.third_cousin++
        } else if (
          rel.explanation.includes('cousin') &&
          rel.explanation.includes('second')
        ) {
          summary.types.second_cousin++
        } else if (
          rel.explanation.includes('cousin') &&
          rel.explanation.includes('once removed')
        ) {
          summary.types.first_cousin_once_removed++
        } else if (
          rel.explanation.includes('cousin') &&
          rel.explanation.includes('twice removed')
        ) {
          summary.types.first_cousin_twice_removed++
        } else if (rel.explanation.includes('cousin')) {
          summary.types.cousin++
        } else if (
          rel.explanation.includes('sibling') &&
          (rel.explanation.includes('uncle') || rel.explanation.includes('aunt'))
        ) {
          summary.types.parental_sibling++
        } else if (
          rel.explanation.includes('sibling-in-law') ||
          rel.explanation.includes('brother-in-law') ||
          rel.explanation.includes('sister-in-law')
        ) {
          summary.types.sibling_in_law++
        } else if (
          rel.explanation.includes('parent-in-law') ||
          rel.explanation.includes('father-in-law') ||
          rel.explanation.includes('mother-in-law')
        ) {
          summary.types.parent_in_law++
        } else if (
          rel.explanation.includes('child-in-law') ||
          rel.explanation.includes('son-in-law') ||
          rel.explanation.includes('daughter-in-law')
        ) {
          summary.types.child_in_law++
        } else if (rel.explanation.includes('sibling')) {
          summary.types.sibling++
        } else if (
          rel.explanation.includes('grandniece') ||
          rel.explanation.includes('grandnephew')
        ) {
          summary.types.grandsibling_child++
        } else if (
          rel.explanation.includes('great-aunt') ||
          rel.explanation.includes('great-uncle')
        ) {
          summary.types.grandparent_sibling++
        } else if (
          rel.explanation.includes('step parent') ||
          rel.explanation.includes('stepfather') ||
          rel.explanation.includes('stepmother')
        ) {
          summary.types.step_parent++
        } else if (
          rel.explanation.includes('step child') ||
          rel.explanation.includes('stepson') ||
          rel.explanation.includes('stepdaughter')
        ) {
          summary.types.step_child++
        } else if (
          rel.explanation.includes('step sibling') ||
          rel.explanation.includes('stepbrother') ||
          rel.explanation.includes('stepsister')
        ) {
          summary.types.step_sibling++
        } else if (
          rel.explanation.includes('half sibling') ||
          rel.explanation.includes('half-brother') ||
          rel.explanation.includes('half-sister')
        ) {
          summary.types.half_sibling++
        } else if (
          rel.explanation.includes('adoptive parent') ||
          rel.explanation.includes('adoptive father') ||
          rel.explanation.includes('adoptive mother')
        ) {
          summary.types.adoptive_parent++
        } else if (
          rel.explanation.includes('adoptive child') ||
          rel.explanation.includes('adoptive son') ||
          rel.explanation.includes('adoptive daughter')
        ) {
          summary.types.adoptive_child++
        } else if (rel.explanation.includes('cousin-in-law')) {
          summary.types.cousin_in_law++
        }
      })

      return summary
    }